import { useState } from 'react';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfigBranches, useConfigCommits } from '@/hooks/useConfigRepository';
import { useAuthStore } from '@/stores/auth.store';
import { formatDateTime } from '@/utils/format';

const MAIN_BRANCH = 'main';
// Radix Select rejects an empty-string item value, so "live configuration" needs a sentinel.
const LIVE_VALUE = 'live';

interface BranchCommitPickerProps {
  configurationId: string;
  value: string;
  onChange: (commitId: string) => void;
}

export const BranchCommitPicker = ({
  configurationId,
  value,
  onChange,
}: BranchCommitPickerProps) => {
  const isAdmin = useAuthStore((state) => state.user?.role === 'admin');
  const [selectedBranch, setSelectedBranch] = useState(MAIN_BRANCH);
  // The repo endpoints are admin-only; the hooks no-op (enabled: false) when configId is undefined.
  const { data: branches, isLoading: branchesLoading } = useConfigBranches(
    isAdmin ? configurationId : undefined,
  );
  const { data: commits, isLoading: commitsLoading } = useConfigCommits(
    isAdmin ? configurationId : undefined,
    selectedBranch,
  );

  if (!isAdmin) {
    return (
      <div className="space-y-1">
        <Input placeholder="Commit UUID" value={value} onChange={(e) => onChange(e.target.value)} />
        <p className="text-muted-foreground text-xs">
          Branch picker requires admin access; paste a commit id or leave empty for the live
          configuration.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <Select value={selectedBranch} onValueChange={setSelectedBranch}>
        <SelectTrigger>
          <SelectValue placeholder={branchesLoading ? 'Loading...' : 'Branch'} />
        </SelectTrigger>
        <SelectContent>
          {branches?.map((branch) => (
            <SelectItem key={branch.id} value={branch.name}>
              {branch.name}
              {branch.status !== 'active' && branch.name !== MAIN_BRANCH
                ? ` (${branch.status})`
                : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={value || LIVE_VALUE}
        onValueChange={(next) => onChange(next === LIVE_VALUE ? '' : next)}
      >
        <SelectTrigger>
          <SelectValue placeholder={commitsLoading ? 'Loading...' : 'Commit'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={LIVE_VALUE}>Live configuration</SelectItem>
          {commits?.map((commit) => (
            <SelectItem key={commit.id} value={commit.id}>
              {commit.id.slice(0, 8)} — {commit.message.slice(0, 40)} (
              {formatDateTime(commit.created_at)})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
